import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { AlertCircle, Image as ImageIcon, Loader2 } from 'lucide-react';

/**
 * Lazily fetches a still frame only when the component enters viewport.
 *
 * @param {object} props
 * @param {() => Promise<string|Blob|null|undefined>} props.loadImage
 * @param {string} [props.frameRequestKey] — when the logical request changes (e.g. new point/timestamp), reset and refetch. Use instead of an unstable `loadImage` ref in effect deps.
 * @param {boolean} [props.frameUnavailable] — expected frame URL missing (e.g. extraction skipped)
 * @param {'neutral'|'expected'} [props.placeholderVariant] — neutral: empty URL is not an error
 */
export function LazyFrameImage({
  loadImage,
  frameRequestKey = '',
  frameUnavailable = false,
  placeholderVariant = 'expected',
  alt = '',
  className = '',
  placeholderClassName = '',
}) {
  const rootRef = useRef(null);
  const loadImageRef = useRef(loadImage);
  loadImageRef.current = loadImage;

  const [isInView, setIsInView] = useState(false);
  const [loading, setLoading] = useState(false);
  const [src, setSrc] = useState(null);
  const [error, setError] = useState(false);
  const [emptyResolved, setEmptyResolved] = useState(false);
  const objectUrlRef = useRef(null);
  const previousFrameKeyRef = useRef();

  // Reset before paint when the requested frame changes (not on first mount) so a new request can load.
  useLayoutEffect(() => {
    if (previousFrameKeyRef.current === undefined) {
      previousFrameKeyRef.current = frameRequestKey;
      return;
    }
    if (previousFrameKeyRef.current === frameRequestKey) {
      return;
    }
    previousFrameKeyRef.current = frameRequestKey;
    setSrc(null);
    setError(false);
    setEmptyResolved(false);
    setLoading(false);
    if (objectUrlRef.current) {
      URL.revokeObjectURL(objectUrlRef.current);
      objectUrlRef.current = null;
    }
  }, [frameRequestKey]);

  useEffect(() => {
    const el = rootRef.current;
    if (!el || src || isInView || frameUnavailable) return undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsInView(true);
            observer.disconnect();
          }
        });
      },
      { rootMargin: '120px' }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [isInView, src, frameUnavailable]);

  // Do not put `loadImage` in deps: parent pass inline functions; identity changes would cancel
  // in-flight work before setSrc and cause a request loop. Read via loadImageRef.
  useEffect(() => {
    if (frameUnavailable || !isInView || src || !loadImageRef.current) return undefined;
    let cancelled = false;
    setLoading(true);
    setError(false);
    setEmptyResolved(false);
    Promise.resolve(loadImageRef.current())
      .then((imageSource) => {
        if (cancelled) return;
        if (!imageSource) {
          if (placeholderVariant === 'expected') {
            setEmptyResolved(true);
          }
          return;
        }
        if (typeof imageSource === 'string') {
          if (objectUrlRef.current) {
            URL.revokeObjectURL(objectUrlRef.current);
            objectUrlRef.current = null;
          }
          setSrc(imageSource);
          return;
        }
        const nextUrl = URL.createObjectURL(imageSource);
        if (objectUrlRef.current) {
          URL.revokeObjectURL(objectUrlRef.current);
        }
        objectUrlRef.current = nextUrl;
        setSrc(nextUrl);
      })
      .catch(() => {
        if (!cancelled) setError(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [isInView, src, placeholderVariant, frameUnavailable, frameRequestKey]);

  useEffect(() => () => {
    if (objectUrlRef.current) {
      URL.revokeObjectURL(objectUrlRef.current);
      objectUrlRef.current = null;
    }
  }, []);

  if (frameUnavailable) {
    return (
      <div ref={rootRef} className={className}>
        <div
          className={`h-full w-full flex items-center justify-center bg-beachvision-surface-low ${placeholderClassName}`}
        >
          <AlertCircle
            className="h-5 w-5 text-beachvision-coral shrink-0"
            strokeWidth={2}
            aria-hidden
          />
          <span className="sr-only">{alt ? `${alt} — ` : ''}Frame unavailable</span>
        </div>
      </div>
    );
  }

  const showErrorIcon = error || emptyResolved;

  return (
    <div ref={rootRef} className={className}>
      {src ? (
        <img src={src} alt={alt} className="h-full w-full object-cover" />
      ) : (
        <div
          className={`h-full w-full flex items-center justify-center bg-beachvision-surface-low ${placeholderClassName}`}
        >
          {loading ? (
            <Loader2 className="h-5 w-5 text-beachvision-muted animate-spin" />
          ) : showErrorIcon ? (
            <AlertCircle
              className="h-5 w-5 text-beachvision-coral shrink-0"
              strokeWidth={2}
              aria-hidden
            />
          ) : (
            <ImageIcon className="h-5 w-5 text-beachvision-muted shrink-0" aria-hidden />
          )}
        </div>
      )}
    </div>
  );
}
